import React, { useEffect, useState, useMemo } from "react";
import journalEntriesAPI from "../../api/journalEntriesAPI";
import { useAuth } from "../../context/AuthContext";
import sanitizeAuditSnapshot from "../../features/journal-entries/utils/sanitizeAuditSnapshot";

const actionStyles = {
  CREATE: "bg-green-100 text-green-800",
  UPDATE: "bg-blue-100 text-blue-800",
  POST: "bg-purple-100 text-purple-800",
  VOID: "bg-red-100 text-red-800"
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric"
  });

const formatDateTime = (date) =>
  new Date(date).toLocaleString();

const groupByDate = (logs) => {
  return logs.reduce((acc, log) => {
    const dateKey = formatDate(log.performedAt);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(log);
    return acc;
  }, {});
};

// Sanitized values are plain JSON-shaped data (objects/arrays/primitives from
// Mongo snapshots), so a structural walk is enough — no need for lodash.
const deepEqual = (a, b) => {
  if (a === b) return true;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }

  if (a && b && typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => deepEqual(a[key], b[key]));
  }

  return false;
};

// `log.changes` is `{ field: { from, to } }` for UPDATE actions — reassemble
// the before/after values per field, sanitize each side (drops audit-internal
// keys and resolves account ids), then re-diff so hidden-field churn (e.g. a
// line's regenerated _id) doesn't show up as a phantom change.
const buildVisibleDiffs = (changes, accountMap) => {
  if (!changes) return [];

  const before = {};
  const after = {};
  Object.entries(changes).forEach(([field, value]) => {
    before[field] = value?.from;
    after[field] = value?.to;
  });

  const sanitizedBefore = sanitizeAuditSnapshot(before, accountMap);
  const sanitizedAfter = sanitizeAuditSnapshot(after, accountMap);

  const fields = new Set([
    ...Object.keys(sanitizedBefore),
    ...Object.keys(sanitizedAfter),
  ]);

  const diffs = [];
  fields.forEach((field) => {
    const from = sanitizedBefore[field];
    const to = sanitizedAfter[field];
    if (!deepEqual(from, to)) diffs.push({ field, from, to });
  });
  return diffs;
};

const JournalEntryAuditTab = ({ entryId, accounts = [] }) => {
  const { isAuthReady } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const accountMap = useMemo(() => {
    const map = {};
    (accounts || []).forEach((a) => {
      if (a?._id) map[a._id] = `${a.accountNumber} - ${a.accountName}`;
    });
    return map;
  }, [accounts]);

  useEffect(() => {
    if (!entryId || !isAuthReady) return;

    const fetchAudit = async () => {
      try {
        setLoading(true);
        const res = await journalEntriesAPI.getAuditTrail(entryId);
        setLogs(res?.data || []);
      } catch (err) {
        setError("Failed to load audit trail");
      } finally {
        setLoading(false);
      }
    };

    fetchAudit();
  }, [entryId, isAuthReady]);

  if (loading) return <div className="text-gray-500">Loading audit trail...</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!logs.length) return <div className="text-gray-500">No audit records found.</div>;

  const groupedLogs = groupByDate(logs);

  return (
    <div className="space-y-8">
      {Object.entries(groupedLogs).map(([date, dateLogs]) => (
        <div key={date}>
          <h3 className="text-sm font-semibold text-gray-600 mb-3 border-b pb-1">
            {date}
          </h3>

          <div className="space-y-4">
            {dateLogs.map((log) => (
              <div
                key={log._id}
                className="relative pl-6 border-l-2 border-gray-200"
              >
                {/* Timeline dot */}
                <div className="absolute -left-2 top-2 w-3 h-3 bg-teal-500 rounded-full"></div>

                <div className="bg-white shadow-sm border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded ${
                        actionStyles[log.action] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {log.action}
                    </span>

                    <span className="text-xs text-gray-500">
                      {formatDateTime(log.performedAt)}
                    </span>
                  </div>

                  <div className="text-xs text-gray-600 mb-2">
                    {log.performedBy?.name}
                    {log.performedBy?.email && ` (${log.performedBy.email})`}
                  </div>

                  {/* VOID Reason */}
                  {log.action === "VOID" && log.snapshot?.reason && (
                    <div className="text-sm text-red-600 font-medium mt-2">
                      Reason: {log.snapshot.reason}
                    </div>
                  )}

                  {/* Reversal detection */}
                  {log.action === "POST" &&
                    log.snapshot?.reversalEntryNumber && (
                      <div className="text-sm text-purple-700 mt-2">
                        Reversed by JE-{log.snapshot.reversalEntryNumber}
                      </div>
                    )}

                  {/* UPDATE changes */}
                  {log.action === "UPDATE" && log.changes && (() => {
                    const diffs = buildVisibleDiffs(log.changes, accountMap);
                    if (diffs.length === 0) {
                      return (
                        <div className="mt-3 text-xs text-gray-500 italic">
                          No visible field changes
                        </div>
                      );
                    }
                    return (
                      <details className="mt-3 text-xs">
                        <summary className="cursor-pointer font-medium text-gray-600">
                          View Changes
                        </summary>
                        <div className="mt-2 overflow-x-auto">
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-500">
                                <th className="pr-4 py-1">Field</th>
                                <th className="pr-4 py-1">From</th>
                                <th className="pr-4 py-1">To</th>
                              </tr>
                            </thead>
                            <tbody>
                              {diffs.map(({ field, from, to }) => (
                                <tr key={field} className="border-t">
                                  <td className="pr-4 py-1 font-medium">
                                    {field}
                                  </td>
                                  <td className="pr-4 py-1">
                                    {JSON.stringify(from)}
                                  </td>
                                  <td className="pr-4 py-1">
                                    {JSON.stringify(to)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    );
                  })()}

                  {/* CREATE / POST snapshot */}
                  {log.action !== "UPDATE" && log.snapshot && (
                    <details className="mt-3 text-xs">
                      <summary className="cursor-pointer text-gray-600">
                        View Snapshot
                      </summary>
                      <pre className="mt-2 bg-gray-50 p-2 rounded border overflow-auto">
                        {JSON.stringify(sanitizeAuditSnapshot(log.snapshot, accountMap), null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default JournalEntryAuditTab;